import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function FineSpirits() {
  const [selectedSpirits, setSelectedSpirits] = useState<string[]>([]);
  const navigate = useNavigate();

  const vodkaSpirits = [
    'Absolut Vodka',
    'Absolut Citron',
    'Absolut Pear'
  ];

  const ginSpirits = [
    'Gordon\'s Gin',
    'Beefeater Gin'
  ];

  const tequillaSpirits = [
    'Patrón Silver',
    'Olmeca Blanco',
    'Olmeca Gold',
    'Olmeca Dark Chocolate'
  ];

  const whiskySpirits = [
    'Legend Premium Whisky',
    'Johnnie Walker Red Label'
  ];

  const liqueursSpirits = [
    'Disaronno Amaretto',
    'Amore di Amore',
    'Amaretto Baileys Irish Cream'
  ];

  const handleCheckboxChange = (spirit: string) => {
    setSelectedSpirits(prev => 
      prev.includes(spirit) 
        ? prev.filter(s => s !== spirit)
        : [...prev, spirit]
    );
  };

  const handleSubmit = () => {
    if (selectedSpirits.length === 0) {
      alert('Please select at least one spirit');
      return;
    }
    
    // Get existing items from localStorage
    const existingItems = localStorage.getItem('selectedOrderItems');
    let allItems: string[] = [];
    
    if (existingItems) {
      try {
        allItems = JSON.parse(existingItems);
      } catch {
        allItems = [];
      }
    }
    
    // Add new selections (avoid duplicates)
    selectedSpirits.forEach(spirit => {
      if (!allItems.includes(spirit)) {
        allItems.push(spirit);
      }
    });
    
    // Save to localStorage
    localStorage.setItem('selectedOrderItems', JSON.stringify(allItems));
    
    // Dispatch custom event to update header count
    window.dispatchEvent(new Event('orderItemsUpdated'));
    
    // Show success message
    const message = document.createElement('div');
    message.className = 'fixed top-24 right-4 bg-green-600 text-white px-6 py-4 rounded-lg shadow-xl z-50 animate-fade-in';
    message.innerHTML = `<i class="ri-check-line mr-2"></i>${selectedSpirits.length} item(s) added to your order`;
    document.body.appendChild(message);
    
    setTimeout(() => {
      message.remove();
    }, 3000);
    
    // Reset selections
    setSelectedSpirits([]);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-black mb-4">
                Fine <span className="text-yellow-600">Spirits</span>
              </h1>
              <p className="text-xl text-gray-600">
                Select from our collection of top premium spirits available in Ghana
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-black mb-6 pb-3 border-b-2 border-yellow-600">
                  VODKA
                </h2>
                <div className="space-y-4">
                  {vodkaSpirits.map((spirit, index) => (
                    <label 
                      key={index}
                      className="flex items-center p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSpirits.includes(spirit)}
                        onChange={() => handleCheckboxChange(spirit)}
                        className="w-5 h-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 focus:ring-2 cursor-pointer"
                      />
                      <span className="ml-4 text-lg text-gray-800 group-hover:text-yellow-600 transition-colors duration-200">
                        {spirit}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-black mb-6 pb-3 border-b-2 border-yellow-600">
                  GIN
                </h2>
                <div className="space-y-4">
                  {ginSpirits.map((spirit, index) => (
                    <label 
                      key={index}
                      className="flex items-center p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSpirits.includes(spirit)}
                        onChange={() => handleCheckboxChange(spirit)}
                        className="w-5 h-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 focus:ring-2 cursor-pointer"
                      />
                      <span className="ml-4 text-lg text-gray-800 group-hover:text-yellow-600 transition-colors duration-200">
                        {spirit}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-black mb-6 pb-3 border-b-2 border-yellow-600">
                  TEQUILLA
                </h2>
                <div className="space-y-4">
                  {tequillaSpirits.map((spirit, index) => (
                    <label 
                      key={index}
                      className="flex items-center p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSpirits.includes(spirit)}
                        onChange={() => handleCheckboxChange(spirit)}
                        className="w-5 h-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 focus:ring-2 cursor-pointer"
                      />
                      <span className="ml-4 text-lg text-gray-800 group-hover:text-yellow-600 transition-colors duration-200">
                        {spirit}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-black mb-6 pb-3 border-b-2 border-yellow-600">
                  WHISKY
                </h2>
                <div className="space-y-4">
                  {whiskySpirits.map((spirit, index) => (
                    <label 
                      key={index}
                      className="flex items-center p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSpirits.includes(spirit)}
                        onChange={() => handleCheckboxChange(spirit)}
                        className="w-5 h-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 focus:ring-2 cursor-pointer"
                      />
                      <span className="ml-4 text-lg text-gray-800 group-hover:text-yellow-600 transition-colors duration-200">
                        {spirit}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-black mb-6 pb-3 border-b-2 border-yellow-600">
                  LIQUEURS
                </h2>
                <div className="space-y-4">
                  {liqueursSpirits.map((spirit, index) => (
                    <label 
                      key={index}
                      className="flex items-center p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSpirits.includes(spirit)}
                        onChange={() => handleCheckboxChange(spirit)}
                        className="w-5 h-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 focus:ring-2 cursor-pointer"
                      />
                      <span className="ml-4 text-lg text-gray-800 group-hover:text-yellow-600 transition-colors duration-200">
                        {spirit}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  variant="primary" 
                  size="lg"
                  onClick={handleSubmit}
                >
                  <i className="ri-check-line mr-2"></i>
                  Submit Selection
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
